import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import axios from '../../../../api/axios';
import { TeleconsultationData } from '../types';

interface UseTeleconsultationReturn {
  teleconsultationData: TeleconsultationData | null;
  isExpired: boolean;
  showReminder: boolean;
  setShowReminder: (value: boolean) => void;
  showRating: boolean;
  setShowRating: (value: boolean) => void;
}

/**
 * Handles fetching teleconsultation data and managing
 * consultation expiry timers and reminder state.
 */
export function useTeleconsultation(
  conversationID: string | undefined,
  decryptedToken: string
): UseTeleconsultationReturn {
  const navigate = useNavigate();

  const [teleconsultationData, setTeleconsultationData] =
    useState<TeleconsultationData | null>(null);
  const [isExpired, setIsExpired] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [showRating, setShowRating] = useState(false);

  // ─── Navigation error handler ─────────────────────────────────────────────

  const handleNavigationError = useCallback(
    (error: any) => {
      if (
        isAxiosError(error) &&
        (error.response?.status === 404 || error.response?.status === 400)
      ) {
        navigate('/not-found');
      } else if (error.status === 403) {
        console.log('You Are Not Authorized to access this resource!');
        navigate('/forbidden');
      } else {
        console.error('Unexpected error:', error);
      }
    },
    [navigate]
  );

  // ─── Fetch data ───────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchTeleconsultationData = async () => {
      if (!conversationID || !decryptedToken) return;
      try {
        const response = await axios.get(`teleconsultation/${conversationID}`, {
          headers: { Authorization: `Bearer ${decryptedToken}` },
        });
        const data = response.data;
        sessionStorage.setItem('teleconsultationData', JSON.stringify(data));
        setTeleconsultationData(data);
      } catch (error) {
        console.error('Gagal mengambil data telekonsultasi:', error);
        handleNavigationError(error);
      }
    };

    fetchTeleconsultationData();
  }, [conversationID, decryptedToken, handleNavigationError]);

  // ─── Expiry & reminder timers ─────────────────────────────────────────────

  useEffect(() => {
    if (!teleconsultationData?.teleconsultation_expired_at) return;

    // Langsung tampilkan rating jika sesi sudah berakhir sebelumnya
    if (
      teleconsultationData.teleconsultation_end_time &&
      !teleconsultationData.teleconsultation_isRating
    ) {
      setShowRating(true);
    }

    let expiredTime = teleconsultationData.teleconsultation_expired_at
      ? Date.parse(
          teleconsultationData.teleconsultation_expired_at.replace(' ', 'T') +
            '+07:00'
        )
      : null;

    const endTime = teleconsultationData.teleconsultation_end_time
      ? Date.parse(teleconsultationData.teleconsultation_end_time)
      : null;

    const timeOffset =
      new Date(teleconsultationData.server_time).getTime() - Date.now();
    const now = Date.now() + timeOffset;

    if (expiredTime === null) expiredTime = Date.now() + 10 * 60 * 1000;

    // Cek apakah sudah expired
    if (endTime && now > endTime) {
      setIsExpired(true);
      return;
    }
    if (now > expiredTime) {
      setIsExpired(true);
      return;
    }

    // Set timer expiry
    const expiredTimeout = setTimeout(
      () => setIsExpired(true),
      expiredTime - now
    );

    // Set timer reminder (5 menit sebelum expired)
    const delayReminder = expiredTime - 5 * 60 * 1000 - now;
    let reminderTimeout: NodeJS.Timeout | null = null;

    if (delayReminder <= 0) {
      setShowReminder(true);
    } else {
      reminderTimeout = setTimeout(() => setShowReminder(true), delayReminder);
    }

    if (teleconsultationData.teleconsultation_end_time) {
      setShowRating(true);
    }

    return () => {
      clearTimeout(expiredTimeout);
      if (reminderTimeout) clearTimeout(reminderTimeout);
    };
  }, [teleconsultationData]);

  return {
    teleconsultationData,
    isExpired,
    showReminder,
    setShowReminder,
    showRating,
    setShowRating,
  };
}
